/* eslint-disable max-len */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { SharedService } from '../../core/services/shared.service';
import { NgxTippyService } from 'ngx-tippy-wrapper';
import _ from 'lodash';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DispatchBoardService } from '../dispatch-board/dispatch-board.service';
import { ModuleAccess } from '../dispatch-board/dispatch-board.interface';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SettingsComponent implements OnInit, OnDestroy {

  @ViewChild('#scrollHandler', { static: false }) scrollHandlerRef: ElementRef;

  moduleName: string;
  selectedDispatchTab: 'JOBS' | 'ROUTES';
  selectedPosition: any;
  isLoading: boolean = false;
  isOptionsAvailable: boolean = false;
  metaData: any;
  randomNumber: number = 0;
  tableViewAttributeMaxCount: number = this._dispatchBoardService.tableViewAttributeMaxCount;
  tableViewSelectedAttributeCount = 0;
  tableViewSelectedRouteAttributeCount = 0;
  tableViewAllAttributeList: any;
  tableViewSelectedAtrributes: any[] = [];
  tableViewOtherAtrributes: any[] = [];
  tableListSearchSubject = {job: new BehaviorSubject<string>(''), route: new BehaviorSubject<string>('')};
  tableListSearchSubscription = {job: null, route: null};
  tableListSearchKeyword = {job: '', route: ''};
  routeTableViewAllAttributeList: any[] = [];
  routeTableViewSelectedAttributes: any[] = [];
  routeTableViewOtherAtrributes: any[] = [];
  tableViewDefaultAttributeKeys: any[] = ['work_order_number@Job-#', 'work_order_title@Job-Title'];
  routeTableViewDefaultAttributeKeys: any[] = ['route_name@Route-Name'];
  moduleFormMap = { 'grid': this._sharedService.gridForm, 'scheduler': this._sharedService.schedulerForm, 'employee': this._sharedService.employeeForm, 'map': this._sharedService.mapForm };
  moduleAccess: ModuleAccess;
  display = {grid: true, employee: true, scheduler: true, map: true};
  cancelSubscription$ = new Subject<void>();
  triggerCardSelectorChangeDetection: boolean = false;
  cardColorOptions = [
    {
      label: 'text.none',
      value: 'none',
      primary: true,
      secondary: false
    },
    {
      label: 'job.category',
      value: 'jobCategory',
      primary: true,
      secondary: true
    },
    {
      label: 'text.job_status',
      value: 'jobStatus',
      primary: true,
      secondary: true
    },
    {
      label: 'text.team',
      value: 'team',
      primary: true,
      secondary: true
    }
  ];
  gridCount = [10, 25, 50, 100];
  tablePositionOptions = [
    {
      label: 'dispatchBoard.settings.grid.table_view.grid_option.left',
      value: 'LEFT'
    },
    {
      label: 'dispatchBoard.settings.grid.table_view.grid_option.bottom',
      value: 'BOTTOM'
    }
  ];
  modeOfTransportOptions = [
    {
      label: 'text.car',
      value: 'CAR'
    },
    {
      label: 'text.truck',
      value: 'TRUCK'
    },
    {
      label: 'text.bike',
      value: 'BIKE'
    },
    {
      label: 'text.walk',
      value: 'WALK'
    }
  ];

  constructor(
    private _localisationService: LocalisationService,
    public _sharedService: SharedService,
    public tippyService: NgxTippyService,
    public _utilityService: UtilityService,
    private _dispatchBoardService: DispatchBoardService,
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<SettingsComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this._localisationService.setDefaultLanguage();
    this.moduleName = this.dialogData?.data.module || 'GRID';
    this.selectedDispatchTab = this.dialogData?.data.tab;
    this.moduleAccess = this._dispatchBoardService.moduleAccess;
    this.display = this.dialogData?.data.display || this.display;
    this.metaData = this._dispatchBoardService.getLocalMetaData();
    if(this.selectedDispatchTab === 'ROUTES' && this._dispatchBoardService.enableMapViewByDefault) {
      this.metaData.map.enable_map_view = true;
    }
    if(this.selectedDispatchTab === 'JOBS' ) {
      if (this._dispatchBoardService.enableMapLayerByDefault.jobs) {
        this.metaData.map.layers.jobs = true;
      }
      if (this._dispatchBoardService.enableMapLayerByDefault.serviceTerritory) {
        this.metaData.map.layers.service_territories = true;
      }
    }
    else if(this.selectedDispatchTab === 'ROUTES') {
      if(this._dispatchBoardService.enableMapLayerByDefault.routes) {
        this.metaData.map.layers.routes = true;
      }
      if(this._dispatchBoardService.enableMapLayerByDefault.routesJob) {
        this.metaData.map.layers.jobs = true;
      }
      if (this._dispatchBoardService.enableMapLayerByDefault.serviceTerritory) {
        this.metaData.map.layers.service_territories = true;
      }
    }
    if (this.metaData) {
      this._sharedService.gridForm.patchValue(this.metaData?.grid);
      this._sharedService.schedulerForm.patchValue(this.metaData?.scheduler);
      this._sharedService.employeeForm.patchValue(this.metaData?.employee);
      this._sharedService.mapForm.patchValue(this.metaData?.map);
    }
    this._sharedService.defaultGridModuleValue = this.metaData;
    this._sharedService.newToDispatchBoard = this.dialogData?.data.newToDispatchBoard || false;;
    this._sharedService.getJobAttributes().then(() => {
      this.tableViewAllAttributeList = this._sharedService.jobAttributes.map(ele => ({ ...ele, isChecked: false, dismissable: this.tableViewDefaultAttributeKeys.includes(ele.key) ? false : true }));
      this.initiateSelectedTableDetails();
      this.initializeAvailableOptions();
      this.isLoading = false;
      this.isOptionsAvailable = true;
      this.changeDetectorRef.markForCheck();
    });
    if (this.selectedDispatchTab === 'ROUTES') {
      this._sharedService.getRouteAttributes().then(() => {
        this.routeTableViewAllAttributeList = this._sharedService.routeAttributes.map((ele: any) => ({ ...ele, isChecked: false, dismissable: this.routeTableViewDefaultAttributeKeys.includes(ele.key) ? false : true }));
        this.initiateSelectedRouteTableDetails();
        this.initializeAvailableRouteOptions();
        this.changeDetectorRef.markForCheck();
      });
    }
    this._sharedService.employeeForm.valueChanges.pipe(takeUntil(this.cancelSubscription$)).subscribe((value) => {
      this.triggerCardSelectorChangeDetection =  !this.triggerCardSelectorChangeDetection;
    });
    this._sharedService.gridForm.valueChanges.pipe(takeUntil(this.cancelSubscription$)).subscribe((value) => {
      this.triggerCardSelectorChangeDetection =  !this.triggerCardSelectorChangeDetection;
    });
    this._sharedService.schedulerForm.valueChanges.pipe(takeUntil(this.cancelSubscription$)).subscribe((value) => {
      this.triggerCardSelectorChangeDetection =  !this.triggerCardSelectorChangeDetection;
    });
    this.initTableSearchDependencies();
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.tableListSearchSubscription.job?.unsubscribe();
    this.tableListSearchSubscription.route?.unsubscribe();
    this.cancelSubscription$.next();
  }

  initializeAvailableOptions(): void {
    const selectedAttributes = [];
    const selectedTableAtrribute: any[] = (this.metaData?.grid.table_view || []).slice(0, this.tableViewAttributeMaxCount);
    selectedTableAtrribute.forEach((row) => {
      selectedAttributes.push(row?.key);
    });
    this.tableViewOtherAtrributes = this.tableViewAllAttributeList.filter(attribute => !selectedAttributes.includes(attribute.key));
    this.changeDetectorRef.markForCheck();
  }

  initializeAvailableRouteOptions(): void {
    const selectedRoutesAttributes = [];
    this.metaData?.grid.route_table_view.forEach((row) => {
      selectedRoutesAttributes.push(row?.key);
    });
    this.routeTableViewOtherAtrributes = this.routeTableViewAllAttributeList.filter(attribute => !selectedRoutesAttributes.includes(attribute.key));
    this.changeDetectorRef.markForCheck();
  }

  initiateSelectedTableDetails(): void {
    this.tableViewSelectedAtrributes = this._sharedService.gridForm.get('table_view').value || [];
    this.tableViewSelectedAtrributes = this.tableViewSelectedAtrributes?.map((attribute: any) => this._dispatchBoardService.setDisplayKeyAndValue(attribute, this.tableViewAllAttributeList));
    this.tableViewSelectedAtrributes = this.tableViewSelectedAtrributes.filter(attribute => _.find(this.tableViewAllAttributeList, { key: attribute.key })).slice(0, this.tableViewAttributeMaxCount);
    this.tableViewSelectedAttributeCount = this.tableViewSelectedAtrributes?.length || 0;
    this.changeDetectorRef.markForCheck();
  }

  initiateSelectedRouteTableDetails(): void {
    this.routeTableViewSelectedAttributes = this._sharedService.gridForm.get('route_table_view').value || [];
    this.routeTableViewSelectedAttributes = this.routeTableViewSelectedAttributes.filter(attribute => _.find(this.routeTableViewAllAttributeList, { key: attribute.key }));
    this.changeDetectorRef.markForCheck();
  }

  onTabChange(selectedTab: string): void {
    this.moduleName = selectedTab;
    this._dispatchBoardService.trackEvent('settings_' + selectedTab);
    this.setScrollToTop();
  }

  setScrollToTop(): void {
    if (this.scrollHandlerRef?.nativeElement) {
      this.scrollHandlerRef.nativeElement.scrollTop = 0;
    }
  }

  closeSettingsDialog(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this._sharedService.saveMetaData(this.moduleAccess);
  }

  openColorProfileDrawer(): void {
    this.dialogRef.close({ openColorProfile: true });
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.tableViewSelectedAtrributes, event.previousIndex, event.currentIndex);
    this.updateTableViewData();
  }

  dropRouteViewList(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.routeTableViewSelectedAttributes, event.previousIndex, event.currentIndex);
    this.updateTableViewData();
  }


  onToggleCheckBox(isChecked: boolean, attribute: any): void {
    if ((this.tableViewSelectedAttributeCount >= this.tableViewAttributeMaxCount) && isChecked) {
      return;
    }
    if (isChecked) {
      this.tableViewSelectedAtrributes.push(attribute);
      _.remove(this.tableViewOtherAtrributes, { key: attribute.key, label: attribute.label });
    }
    else {
      this.tableViewOtherAtrributes.splice(0, 0, attribute);
      _.remove(this.tableViewSelectedAtrributes, { key: attribute.key, label: attribute.label });
    }
    this.tableViewSelectedAttributeCount = this.tableViewSelectedAtrributes?.length || 0;
    this.updateTableViewData();
    this.changeDetectorRef.detectChanges();
  }

  onToggleRouteAttributeCheckBox(isChecked: boolean, attribute: any): void {
    this.tableViewSelectedRouteAttributeCount = isChecked === true ? this.tableViewSelectedRouteAttributeCount + 1 : this.tableViewSelectedRouteAttributeCount - 1;
    if (isChecked) {
      this.routeTableViewSelectedAttributes.push(attribute);
      _.remove(this.routeTableViewOtherAtrributes, { key: attribute.key, label: attribute.label });
    }
    else {
      this.routeTableViewOtherAtrributes.push(attribute);
      _.remove(this.routeTableViewSelectedAttributes, { key: attribute.key, label: attribute.label });
    }
    this.updateRouteTableViewData();
    this.changeDetectorRef.markForCheck();
  }


  updateTableViewData(): void {
    this._sharedService.updateFormFields(this._sharedService.gridForm, 'table_view', this.tableViewSelectedAtrributes);
    this.randomNumber = Math.random();
  }
  updateRouteTableViewData(): void {
    this._sharedService.updateFormFields(this._sharedService.gridForm, 'route_table_view', this.routeTableViewSelectedAttributes);
    this.randomNumber = Math.random();
  }

  initTableSearchDependencies(): void {
    this.tableListSearchSubscription.job = this.tableListSearchSubject.job.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: any) => {
        this.tableListSearchKeyword.job = value;
      });
      this.tableListSearchSubscription.route = this.tableListSearchSubject.route.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: any) => {
        this.tableListSearchKeyword.route = value;
      });
  }

  onSearchTableList(tab: 'JOBS' | 'ROUTES', value: any): void {
    if(tab === 'JOBS') {
      this.tableListSearchSubject.job.next(value);
    }
    else if(tab === 'ROUTES'){
      this.tableListSearchSubject.route.next(value);
    }
  }
}
